/**
 * MapView - Wraps react-leaflet with a Nominatim-backed geocoder so
 * ScheduledEvents can render as map pins by their address.
 *
 * Why Nominatim?
 *   - Free, open data (OpenStreetMap), no API key.
 *   - Per their usage policy: ≤1 request/sec, custom user-agent, must
 *     cache results. We respect all three: serial fetch loop with a
 *     1.05s gap, browser-default UA (origin-stamped is sufficient
 *     for our scale), and localStorage cache keyed on the full
 *     address string.
 *   - Anything more aggressive will require switching to a paid
 *     geocoder; that's a server-side concern and out of scope here.
 *
 * Pins:
 *   - Color-coded by record type (work_order = orange, inspection = blue),
 *     using a small DivIcon so we don't have to ship marker icon assets.
 *   - Click invokes the parent's onSelectEvent so the host page can
 *     open its own details modal — same flow as the Calendar. We do
 *     NOT render an inline Leaflet popup here; doing both would
 *     stack a popup on top of the modal.
 *
 * Events that can't be geocoded (server returned no address, or
 * Nominatim couldn't resolve it) are surfaced via the
 * `ungeocodedCount` returned to the parent so the page can render
 * a "N events without locations" notice.
 *
 * Library quirk: react-leaflet requires Leaflet's CSS bundled before
 * any Map component renders, otherwise tiles are unstyled. Imported
 * here once; consumers don't need to repeat.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
/* Touch-target overrides for Leaflet's own chrome — scoped to .leaflet-*. */
import './MapView.responsive.css';
import type { ScheduledEvent } from '../../../services/scheduledEventsService';

/* ============================================
 * GEOCODING
 * ============================================ */

interface GeocodeResult {
    lat: number;
    lng: number;
}

/** localStorage key for the persistent geocode cache. */
const GEOCODE_CACHE_KEY = 'gbrands_geocode_cache_v1';

/** Read the geocode cache from localStorage (empty object on miss). */
function readCache(): Record<string, GeocodeResult> {
    try {
        const raw = localStorage.getItem(GEOCODE_CACHE_KEY);
        return raw ? (JSON.parse(raw) as Record<string, GeocodeResult>) : {};
    } catch {
        return {};
    }
}

/** Persist the cache to localStorage; swallow quota errors silently. */
function writeCache(cache: Record<string, GeocodeResult>): void {
    try {
        localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(cache));
    } catch {
        // Quota exceeded — drop the cache silently rather than throwing
        // up to the user. Worst case: re-geocoding on next visit.
    }
}

/**
 * Build the address string we feed to the geocoder + use as the cache
 * key. Returns null when the event has no usable address.
 */
function buildAddressKey(event: ScheduledEvent): string | null {
    const parts = [event.address, event.city, event.state, event.zip].filter(Boolean) as string[];
    if (parts.length === 0) return null;
    return parts.join(', ');
}

/**
 * Resolve an address to lat/lng via Nominatim. Returns null on any
 * failure (rate limit, network, no result). Caller is responsible
 * for honoring the 1 req/sec throttle.
 */
async function geocodeAddress(address: string): Promise<GeocodeResult | null> {
    try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(address)}`;
        const res = await fetch(url, {
            headers: { Accept: 'application/json' },
        });
        if (!res.ok) return null;
        const json = (await res.json()) as Array<{ lat: string; lon: string }>;
        if (!json.length) return null;
        const first = json[0];
        return { lat: parseFloat(first.lat), lng: parseFloat(first.lon) };
    } catch {
        return null;
    }
}

/* ============================================
 * AUTO-FIT BOUNDS
 * ============================================ */

/**
 * Helper component rendered inside <MapContainer> that re-fits the
 * map bounds whenever the marker set changes. Without this the map
 * stays at its initial center even after geocoding returns 50 pins
 * scattered across the country.
 */
const FitBounds: React.FC<{ points: GeocodeResult[] }> = ({ points }) => {
    const map = useMap();
    useEffect(() => {
        if (points.length === 0) return;
        const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng] as [number, number]));
        // padding gives the outermost markers some breathing room
        // around the viewport edges.
        map.fitBounds(bounds, { padding: [40, 40] });
    }, [map, points]);
    return null;
};

/* ============================================
 * PIN ICONS
 * ============================================ */

/**
 * Build a colored DivIcon for the given record type. Cached by type
 * so we don't allocate a new icon per pin per render.
 */
const ICON_CACHE: Record<ScheduledEvent['recordType'], L.DivIcon> = {
    work_order: L.divIcon({
        className: '',
        iconSize: [22, 22],
        iconAnchor: [11, 22],
        html: `<div style="
            width: 22px;
            height: 22px;
            background-color: #F9550E;
            border: 2px solid white;
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        "></div>`,
    }),
    inspection: L.divIcon({
        className: '',
        iconSize: [22, 22],
        iconAnchor: [11, 22],
        html: `<div style="
            width: 22px;
            height: 22px;
            background-color: #1d4ed8;
            border: 2px solid white;
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        "></div>`,
    }),
};

/* ============================================
 * COMPONENT
 * ============================================ */

export interface MapViewProps {
    /** Events to render as map pins (after geocoding). */
    events: ScheduledEvent[];
    /** Callback fired when an event's pin is clicked. Defaults to opening the popup inline. */
    onSelectEvent?: (event: ScheduledEvent) => void;
    /** Reported count of events whose address couldn't be geocoded
     *  (changed via the parent's setter when this list updates). */
    onUngeocodedChange?: (count: number) => void;
    /** Optional outer className. */
    className?: string;
}

interface PinnedEvent {
    event: ScheduledEvent;
    coords: GeocodeResult;
}

/**
 * Default map center — continental US so the initial render isn't
 * empty even before geocoding finishes. FitBounds takes over once
 * any pins resolve.
 */
const DEFAULT_CENTER: [number, number] = [39.8283, -98.5795];
const DEFAULT_ZOOM = 4;

export const MapView: React.FC<MapViewProps> = ({
    events,
    onSelectEvent,
    onUngeocodedChange,
    className,
}) => {
    /* Pinned events — those for which we have lat/lng. Updated
     * incrementally as geocoding progresses. */
    const [pinned, setPinned] = useState<PinnedEvent[]>([]);
    const [ungeocodedCount, setUngeocodedCount] = useState(0);
    const [isGeocoding, setIsGeocoding] = useState(false);

    /* Ref pointing at the latest events array so the geocoding loop
     * can short-circuit when the parent swaps in a new set. */
    const latestEventsRef = useRef(events);
    latestEventsRef.current = events;

    /* Run the geocoder whenever the events array changes. We resolve
     * cached entries synchronously and queue the rest serially with a
     * small inter-request delay to respect Nominatim's policy. */
    useEffect(() => {
        let cancelled = false;
        const cache = readCache();
        const resolved: PinnedEvent[] = [];
        const pending: Array<{ event: ScheduledEvent; key: string }> = [];

        for (const event of events) {
            const key = buildAddressKey(event);
            if (!key) continue;
            if (cache[key]) {
                resolved.push({ event, coords: cache[key] });
            } else {
                pending.push({ event, key });
            }
        }

        // Initial state — show whatever's already cached immediately.
        setPinned(resolved);
        const initialUngeocoded =
            events.filter((e) => !buildAddressKey(e)).length + pending.length;
        setUngeocodedCount(initialUngeocoded);
        if (pending.length === 0) {
            setIsGeocoding(false);
            onUngeocodedChange?.(initialUngeocoded);
            return;
        }

        // Serial fetch loop with a small gap between requests.
        setIsGeocoding(true);
        let stillPending = pending.length;
        (async () => {
            for (const { event, key } of pending) {
                if (cancelled) return;
                if (latestEventsRef.current !== events) {
                    // Parent swapped the events array; abandon this loop —
                    // the new effect run owns the geocoding from here.
                    return;
                }
                const result = await geocodeAddress(key);
                if (cancelled) return;
                if (result) {
                    cache[key] = result;
                    writeCache(cache);
                    setPinned((prev) => [...prev, { event, coords: result }]);
                    stillPending -= 1;
                    setUngeocodedCount((c) => c - 1);
                } else {
                    // Failed lookup — leave it counted as ungeocoded.
                    stillPending -= 1;
                }
                onUngeocodedChange?.(stillPending);
                // Throttle to ~1 req/sec per Nominatim's usage policy.
                await new Promise((r) => setTimeout(r, 1050));
            }
            setIsGeocoding(false);
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [events]);

    /* Stable lookup of pin coordinates for the FitBounds helper. */
    const points = useMemo(() => pinned.map((p) => p.coords), [pinned]);

    return (
        <div className={['map-view', 'relative w-full h-[640px] bg-brand-surface rounded-md overflow-hidden border border-brand-border', className ?? ''].join(' ')} data-ref="organism-map-view">
            <MapContainer
                center={DEFAULT_CENTER}
                zoom={DEFAULT_ZOOM}
                className="h-full w-full"
                scrollWheelZoom
            >
                {/* Free OpenStreetMap tiles. Required attribution per their license. */}
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                <FitBounds points={points} />

                {pinned.map(({ event, coords }) => (
                    <Marker
                        key={event.id}
                        position={[coords.lat, coords.lng]}
                        // Fallback to the work_order icon if recordType is
                        // somehow unrecognized — react-leaflet v5 forwards
                        // `icon: undefined` straight to L.Marker, which
                        // overrides Leaflet's prototype default and crashes
                        // _initIcon with "Cannot read properties of undefined".
                        icon={ICON_CACHE[event.recordType] ?? ICON_CACHE.work_order}
                        eventHandlers={{
                            click: () => onSelectEvent?.(event),
                        }}
                    />
                ))}
            </MapContainer>

            {/* Geocoding progress + ungeocoded notice overlay */}
            <div className="map-view__overlay absolute top-12px right-12px z-[400] flex flex-col items-end gap-4px">
                {isGeocoding && (
                    <div className="bg-white text-12px text-brand-text-muted px-10px py-6px rounded-md shadow border border-brand-border">
                        Resolving locations…
                    </div>
                )}
                {ungeocodedCount > 0 && !isGeocoding && (
                    <div className="bg-white text-12px text-brand-text-muted px-10px py-6px rounded-md shadow border border-brand-border">
                        {ungeocodedCount} event{ungeocodedCount === 1 ? '' : 's'} without map locations
                    </div>
                )}
            </div>
        </div>
    );
};

export default MapView;
